import React, { useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { Edit, Trash } from 'lucide-react';
import Drawer from '../Drawer';
import CategoryAdd from './CategoryAdd';
import CategoryEdit from './CategoryEdit';
import CustomAlert from '../CustomAlert';

const apiUrl = process.env.REACT_APP_API_URL;

export default function CategoriesList() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'success' });

  const fetchCategories = () => {
    setLoading(true);
    const token = localStorage.getItem('token');

    fetch(`${apiUrl}/categories`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then((res) => res.json())
      .then((data) => setCategories(Array.isArray(data) ? data : []))
      .catch((err) => console.error('Failed to fetch categories:', err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Deleting confirms in place — the list stays put, so the row simply
  // disappears once the server has accepted it.
  const confirmDelete = () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    const token = localStorage.getItem('token');

    fetch(`${apiUrl}/categories/${deleteTarget.id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Категоријата не е избришана');
        setDeleteTarget(null);
        setAlert({ show: true, type: 'success', message: data.message || 'Категоријата е избришана.' });
        fetchCategories();
      })
      .catch((err) => {
        setDeleteTarget(null);
        setAlert({ show: true, type: 'error', message: err.message || 'Категоријата не е избришана' });
      })
      .finally(() => setDeleting(false));
  };

  const allColumns = useMemo(() => [
    { name: 'ID', selector: (row) => row.id, sortable: true, width: '80px' },
    {
      name: 'Назив',
      selector: (row) => row.name,
      sortable: true,
      grow: 2,
      cell: (row) => <span className="font-semibold text-gray-900">{row.name}</span>,
    },
    { name: 'Опис', selector: (row) => row.description || '-', sortable: true },
    {
      name: 'Данок',
      selector: (row) => (row.tax_id != null ? `${parseFloat(row.tax_id).toFixed(0)}%` : '—'),
      sortable: true,
      width: '100px',
    },
    {
      name: 'Креирана на',
      selector: (row) => (row.created_at ? new Date(row.created_at).toLocaleDateString('mk-MK') : ''),
      sortable: true,
      width: '140px',
    },
    {
      name: 'Акции',
      cell: (row) => (
        <div className="flex gap-2 items-center">
          <button
            onClick={() => setEditId(row.id)}
            title="Уреди"
            className="p-2 rounded-lg text-indigo-600 hover:bg-indigo-50 transition-colors"
          >
            <Edit size={18} />
          </button>
          <button
            onClick={() => setDeleteTarget(row)}
            title="Избриши"
            className="p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
          >
            <Trash size={18} />
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: '120px',
    },
  ], []);

  if (loading) {
    return (
      <div className="h-full bg-gray-50 flex justify-center items-center">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 mx-auto text-blue-500 mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600 text-lg">Вчитување на категории...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {/* Action Bar */}
        <div className="flex flex-wrap gap-2 sm:gap-3 mb-4 sm:mb-6 justify-start sm:justify-end">
          <div className="flex gap-2 items-center flex-wrap justify-start sm:justify-end w-full sm:w-auto">
            <button
              onClick={() => setAddOpen(true)}
              className="w-full sm:w-auto justify-center px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium text-sm transition-colors flex items-center space-x-2"
              title="Нова категорија"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Нова Категорија</span>
            </button>
          </div>
        </div>

        {/* Categories Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden max-w-full">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <h2 className="text-sm font-semibold text-gray-900">Категории</h2>
          </div>
          <DataTable
            columns={allColumns}
            data={categories}
            pagination
            highlightOnHover={false}
            striped={false}
            responsive
            persistTableHead
            noDataComponent={
              <div className="p-12 text-center">
                <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                <p className="text-gray-600">Нема пронајдени категории</p>
              </div>
            }
          />
        </div>
      </div>

      <Drawer open={addOpen} onClose={() => setAddOpen(false)} title="Нова категорија">
        <CategoryAdd
          onSuccess={() => {
            setAddOpen(false);
            fetchCategories();
          }}
        />
      </Drawer>

      <Drawer open={editId !== null} onClose={() => setEditId(null)} title="Измени категорија">
        {/* key remounts the form when another row is picked, so it refetches. */}
        <CategoryEdit
          key={editId}
          id={editId}
          onSuccess={() => {
            setEditId(null);
            fetchCategories();
          }}
        />
      </Drawer>

      {/* Delete confirmation — replaces the old /categories/delete/:id page. */}
      <CustomAlert
        isOpen={deleteTarget !== null}
        type="error"
        title="Избриши категорија"
        message={`Дали сте сигурни дека сакате да ја избришете „${deleteTarget?.name ?? ''}“?`}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        showConfirmButton={true}
        confirmText={deleting ? 'Се брише...' : 'Избриши'}
        cancelText="Откажи"
      />

      <CustomAlert
        isOpen={alert.show}
        type={alert.type}
        title={alert.type === 'success' ? 'Успешно' : 'Грешка'}
        message={alert.message}
        onClose={() => setAlert({ show: false, message: '', type: 'success' })}
      />
    </div>
  );
}
